/* De instellingen van de site (website.site_settings, ADR 0024): één rij per instelling, met een
 * sleutel (`key`) en een waarde als tekst (`value`). De site leest ze op elke pagina.
 *
 * De regels:
 *   - Alleen de sleutels hieronder. Wat er verder in de tabel staat (bijvoorbeeld
 *     analytics_excluded_ips), wordt niet gelezen, niet getoond en niet geschreven.
 *   - Een instelling die niemand aanraakt, wordt niet geschreven.
 *   - Heeft een instelling nog geen rij, dan maakt het opslaan hem aan. Een bestaande rij wordt
 *     bijgewerkt. Er wordt nooit een rij verwijderd: leegmaken is de waarde leeg zetten.
 *   - Alleen wat is aangeraakt, wordt nagekeken. Oude inhoud houdt het opslaan van iets anders niet tegen.
 *
 * Wat de site met een waarde doet, staat in de site-repo: lib/telefoon.ts (phone), lib/tools.ts (de
 * rekentools), lib/getallen.ts (de getallenbalk), Footer en StructuredData (de rest).
 *
 * De client komt als argument binnen, zoals in ./website, zodat de toetsen hem kunnen nabootsen. */

import type { Controle, WebsiteClient } from './website';

export const TABEL = 'site_settings';

export interface Instelling {
  sleutel: string;
  label: string;
  /** Een schakelaar staat aan tenzij de waarde precies 'false' is: zo leest de site hem. */
  soort: 'tekst' | 'schakelaar';
  uitleg?: string;
  /** Een waarschuwing die opvalt: wat er op de site gebeurt zodra hier iets staat. */
  letOp?: string;
  voorbeeld?: string;
  /** Wat er mis is met een ingevulde waarde, of null. Een lege waarde wordt niet nagekeken. */
  keur?: (waarde: string) => string | null;
}

export interface Groep {
  sleutel: 'contact' | 'rekentools' | 'getallenbalk';
  titel: string;
  uitleg: string;
  instellingen: Instelling[];
}

/* ── Contact ───────────────────────────────────────────────────────────────── */

/** Een heel adres dat met https:// begint. Ook gebruikt voor de lijsten (./lijsten). */
export const isAdres = (waarde: string): boolean => {
  try {
    return new URL(waarde).protocol === 'https:';
  } catch {
    return false;
  }
};

const CONTACT: Instelling[] = [
  {
    sleutel: 'phone', label: 'Telefoonnummer', soort: 'tekst', voorbeeld: '+31 (0)73 000 00 00',
    letOp: 'Let op: zodra hier een nummer staat, toont de site het op elke pagina: in de kop, in de voet, bij contact en in de gegevens voor zoekmachines. Vul het alleen in als dat de bedoeling is. Leeg betekent: nergens een nummer.',
    keur: (w) => (/\d/.test(w) ? null : 'Een telefoonnummer bevat cijfers.'),
  },
  {
    sleutel: 'email', label: 'E-mailadres', soort: 'tekst', voorbeeld: 'info@greenvision.energy',
    keur: (w) => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(w) ? null : 'Dit is geen e-mailadres.'),
  },
  { sleutel: 'company_name', label: 'Bedrijfsnaam', soort: 'tekst' },
  {
    sleutel: 'kvk', label: 'KvK-nummer', soort: 'tekst',
    keur: (w) => (/^\d{8}$/.test(w) ? null : 'Een KvK-nummer bestaat uit 8 cijfers.'),
  },
  {
    sleutel: 'linkedin_url', label: 'LinkedIn', soort: 'tekst', voorbeeld: 'https://www.linkedin.com/company/...',
    keur: (w) => (isAdres(w) ? null : 'Vul het hele adres in. Het begint met https://.'),
  },
  { sleutel: 'address_street', label: 'Straat en huisnummer', soort: 'tekst' },
  { sleutel: 'address_zip', label: 'Postcode', soort: 'tekst' },
  { sleutel: 'address_city', label: 'Plaats', soort: 'tekst' },
  { sleutel: 'address_country', label: 'Land', soort: 'tekst' },
  {
    sleutel: 'logo_url', label: 'Logo', soort: 'tekst', voorbeeld: '/media/bestandsnaam.png',
    uitleg: 'De site gebruikt deze instelling nu niet: het logo staat in de code van de site.',
    keur: (w) => (w.startsWith('/media/') ? null : 'Een afbeelding komt uit de eigen media en begint met /media/.'),
  },
];

/* ── Rekentools ────────────────────────────────────────────────────────────── */

export const ALLE_TOOLS = 'tools_enabled';

/* De drie tools die de site kent (TOOL_SETTING_KEYS in lib/tools.ts), met de naam die op de site staat. */
const REKENTOOLS: Instelling[] = [
  { sleutel: ALLE_TOOLS, label: 'Alle rekentools', soort: 'schakelaar', uitleg: 'Staat dit uit, dan is er geen enkele rekentool op de site, wat er hieronder ook staat.' },
  { sleutel: 'tool_ere_calculator_enabled', label: 'ERE calculator', soort: 'schakelaar' },
  { sleutel: 'tool_truck_compare_enabled', label: 'Diesel vs. Elektrisch calculator', soort: 'schakelaar' },
  { sleutel: 'tool_flex_e_subsidie_enabled', label: 'Flex-E quickscan', soort: 'schakelaar' },
];

/** Zo leest de site een schakelaar: aan, tenzij de waarde precies 'false' is. Geen rij is dus aan. */
export const staatAan = (waarde: string | null | undefined): boolean => waarde !== 'false';

/* ── Getallenbalk ──────────────────────────────────────────────────────────── */

export const AANTAL_VAKKEN = 4;
export const vakSleutels = (vak: number) => ({ waarde: `stat_${vak}_value`, bijschrift: `stat_${vak}_label` });

const GETALLENBALK: Instelling[] = Array.from({ length: AANTAL_VAKKEN }, (_, i) => i + 1).flatMap((vak): Instelling[] => [
  { sleutel: vakSleutels(vak).waarde, label: `Vak ${vak}: waarde`, soort: 'tekst' },
  { sleutel: vakSleutels(vak).bijschrift, label: `Vak ${vak}: bijschrift`, soort: 'tekst' },
]);

export type VakStaat = 'af' | 'leeg' | 'geen_waarde' | 'geen_cijfer' | 'geen_bijschrift';

/** Telt een vak mee op de site? Dezelfde regel als getallen() in lib/getallen.ts van de site: een
 *  waarde met een cijfer erin én een bijschrift. Spaties tellen niet. */
export function vakStaat(waarden: Record<string, string | null | undefined>, vak: number): VakStaat {
  const waarde = (waarden[vakSleutels(vak).waarde] ?? '').trim();
  const bijschrift = (waarden[vakSleutels(vak).bijschrift] ?? '').trim();
  if (!waarde && !bijschrift) return 'leeg';
  if (!waarde) return 'geen_waarde';
  if (!/\d/.test(waarde)) return 'geen_cijfer';
  if (!bijschrift) return 'geen_bijschrift';
  return 'af';
}

export const VAK_ZIN: Record<VakStaat, string> = {
  af: 'Af: dit vak telt mee.',
  leeg: 'Leeg: dit vak telt niet mee.',
  geen_waarde: 'De waarde ontbreekt: dit vak telt niet mee.',
  geen_cijfer: 'In de waarde staat geen cijfer: dit vak telt niet mee.',
  geen_bijschrift: 'Het bijschrift ontbreekt: dit vak telt niet mee.',
};

/* ── De groepen, zoals ze op het scherm staan ──────────────────────────────── */

export const GROEPEN: Groep[] = [
  {
    sleutel: 'contact', titel: 'Contact',
    uitleg: 'De gegevens in de voet van de site, bij contact en in de gegevens voor zoekmachines. Laat je een vak leeg, dan gebruikt de site wat er in de code staat, of laat het weg.',
    instellingen: CONTACT,
  },
  {
    sleutel: 'rekentools', titel: 'Rekentools',
    uitleg: 'De calculators onder Tools. Een tool die uit staat, is op de site niet te vinden: de pagina bestaat dan niet en de verwijzingen ernaar vallen weg.',
    instellingen: REKENTOOLS,
  },
  {
    sleutel: 'getallenbalk', titel: 'Getallenbalk op de homepage',
    uitleg: 'Een balk met hooguit vier getallen over Greenvision, direct onder de opening van de homepage. Een vak telt alleen mee als het af is: een waarde met een cijfer erin én een bijschrift. Een vak dat niet af is, valt weg en de rest schuift aan. Is geen enkel vak af, dan is er geen balk.',
    instellingen: GETALLENBALK,
  },
];

export const INSTELLINGEN: Instelling[] = GROEPEN.flatMap((g) => g.instellingen);
export const SLEUTELS: string[] = INSTELLINGEN.map((i) => i.sleutel);

/* ── Lezen, nakijken, opslaan ──────────────────────────────────────────────── */

/** Wat er in de database staat: alleen de sleutels die een rij hebben. De waarde kan leeg (null) zijn. */
export type Stand = Record<string, string | null>;
/** Wat er in het formulier staat: per bekende sleutel de tekst in het vak. */
export type Concept = Record<string, string>;

const heeftRij = (stand: Stand, sleutel: string): boolean => Object.prototype.hasOwnProperty.call(stand, sleutel);

/** Wat er in het vak staat zolang niemand iets doet: de waarde uit de database, of niets. */
export const invoer = (stand: Stand, sleutel: string): string => stand[sleutel] ?? '';

export const beginConcept = (stand: Stand): Concept => Object.fromEntries(SLEUTELS.map((s) => [s, invoer(stand, s)]));

/** Is dit vak aangeraakt? Vergeleken met wat er stond toen het formulier openging. */
const aangeraakt = (stand: Stand, concept: Concept, sleutel: string): boolean =>
  typeof concept[sleutel] === 'string' && concept[sleutel] !== invoer(stand, sleutel);

export interface Wijziging {
  sleutel: string;
  /** null = leeg. De rij blijft bestaan. */
  waarde: string | null;
  /** Er is nog geen rij: het opslaan maakt hem aan. */
  nieuw: boolean;
}

/** Wat er geschreven moet worden: alleen bekende sleutels die zijn aangeraakt en echt anders zijn.
 *  Spaties aan de randen vallen weg. Een leeg vak zonder rij maakt geen rij aan. */
export function wijzigingen(stand: Stand, concept: Concept): Wijziging[] {
  const uit: Wijziging[] = [];
  for (const sleutel of SLEUTELS) {
    if (!aangeraakt(stand, concept, sleutel)) continue;
    const waarde = concept[sleutel].trim();
    if (waarde === invoer(stand, sleutel)) continue;
    uit.push({ sleutel, waarde: waarde === '' ? null : waarde, nieuw: !heeftRij(stand, sleutel) });
  }
  return uit;
}

/** Wat er mis is, per sleutel. Alleen wat is aangeraakt, wordt nagekeken. */
export function controleerInstellingen(stand: Stand, concept: Concept): Controle {
  const fouten: Record<string, string> = {};
  for (const instelling of INSTELLINGEN) {
    if (!aangeraakt(stand, concept, instelling.sleutel)) continue;
    const waarde = concept[instelling.sleutel].trim();
    if (instelling.soort === 'schakelaar') {
      if (waarde !== 'true' && waarde !== 'false') fouten[instelling.sleutel] = `${instelling.label} kan alleen aan of uit staan.`;
      continue;
    }
    const fout = waarde && instelling.keur ? instelling.keur(waarde) : null;
    if (fout) fouten[instelling.sleutel] = `${instelling.label}: ${fout}`;
  }
  return { ok: Object.keys(fouten).length === 0, fouten };
}

function ofFout<T>(antwoord: { data: T | null; error: { message: string } | null }, wat: string): T {
  if (antwoord.error) throw new Error(`${wat}: ${antwoord.error.message}`);
  return antwoord.data as T;
}

type SettingRij = { key: string; value: string | null };

/** Leest alleen de bekende sleutels. Een rij met een andere sleutel komt de browser niet in. */
export async function haalInstellingen(client: WebsiteClient): Promise<Stand> {
  const antwoord = await client.schema('website').from(TABEL).select('key, value').in('key', SLEUTELS);
  const rijen = ofFout<SettingRij[]>(antwoord, 'Instellingen lezen') ?? [];
  const stand: Stand = {};
  for (const rij of rijen) if (SLEUTELS.includes(rij.key)) stand[rij.key] = rij.value ?? null;
  return stand;
}

export interface InstellingenOpgeslagen {
  /** De stand na het opslaan. */
  stand: Stand;
  geschreven: Wijziging[];
}

/** Slaat de gewijzigde instellingen op, in één schrijfactie: alles of niets. Een sleutel zonder rij
 *  wordt aangemaakt, een bestaande bijgewerkt (upsert op `key`). Is er niets gewijzigd, dan gaat er
 *  niets naar de database. Er wordt hier nooit een rij verwijderd. */
export async function slaInstellingenOp(client: WebsiteClient, stand: Stand, concept: Concept): Promise<InstellingenOpgeslagen> {
  const controle = controleerInstellingen(stand, concept);
  if (!controle.ok) throw new Error(Object.values(controle.fouten)[0]);
  const geschreven = wijzigingen(stand, concept);
  if (geschreven.length === 0) return { stand, geschreven };

  const rijen: SettingRij[] = geschreven.map((w) => ({ key: w.sleutel, value: w.waarde }));
  const antwoord = await client.schema('website').from(TABEL).upsert(rijen, { onConflict: 'key' }).select('key, value');
  const terug = ofFout<SettingRij[]>(antwoord, 'Instellingen opslaan') ?? [];
  // RLS kan zonder fout weigeren: elke geschreven sleutel hoort terug te komen.
  const gekregen = new Set(terug.map((r) => r.key));
  if (geschreven.some((w) => !gekregen.has(w.sleutel))) {
    throw new Error('De instellingen zijn niet opgeslagen. Je hebt hier geen schrijfrecht.');
  }
  const nieuw: Stand = { ...stand };
  for (const w of geschreven) nieuw[w.sleutel] = w.waarde;
  return { stand: nieuw, geschreven };
}
